import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';

const AuthContext = createContext(null);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // iter65 — While the Google OAuth callback is in flight, defer the
    // /auth/me probe to AuthCallback so we don't race the code-exchange
    // and prematurely flip the user to null (which causes ProtectedRoute
    // to redirect to /login before the JWT cookie is even set).
    if (typeof window !== 'undefined') {
      const onCallback = window.location.pathname.startsWith('/auth/google/callback');
      const hasCode = new URLSearchParams(window.location.search).has('code');
      if (onCallback && hasCode) {
        // AuthCallback will call setUser() itself once the exchange
        // completes. Mark loading=false so any non-protected children
        // (like the spinner) can render.
        setLoading(false);
        return;
      }
    }
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    // Mirror the OAuth path: persist any token the backend returned so
    // the Bearer fallback in api.js works even if the cookie is dropped.
    if (data?.token) {
      try { localStorage.setItem('token', data.token); } catch { /* noop */ }
    }
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    if (data?.token) {
      try { localStorage.setItem('token', data.token); } catch { /* noop */ }
    }
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* noop */ }
    try { localStorage.removeItem('token'); } catch { /* noop */ }
    setUser(null);
  };

  const value = { user, loading, login, register, logout, refreshUser: loadMe, setUser };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
